import { useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { adjustLightness, deriveDarkPalette, hexToHSL } from '../utils/lottieColorUtils';

// Convert a hex color to a bare "r, g, b" triplet for use inside rgba(var(--x), alpha)
const hexToRgbStr = (hex) => {
  const h = hex.replace('#', '');
  return `${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}`;
};

const BrandingProvider = () => {
  const { currentBranding, isCustomBranding } = useSelector(state => state['Settings']);

  // Update CSS custom properties for theming
  // Derives both light --sa-* and dark --sa-dark-* tokens from primary color.
  // Default Sales AI branding uses CSS values from theme.css.
  // useLayoutEffect (not useEffect) so CSS vars are written before any child useEffect reads them via getCssVar.
  useLayoutEffect(() => {
    if (!currentBranding) return;

    const root = document.documentElement;
    const primaryColor = currentBranding['primaryColor'];

    if (primaryColor && isCustomBranding) {
      // Legacy variables
      root.style.setProperty('--primary-color', primaryColor);

      // Derive --sa-* design tokens from primary color
      root.style.setProperty('--sa-maroon-mid', primaryColor);
      root.style.setProperty('--sa-primary', adjustLightness(primaryColor, 12));
      root.style.setProperty('--sa-primary-hover', adjustLightness(primaryColor, 6));
      root.style.setProperty('--sa-primary-soft', adjustLightness(primaryColor, 20));
      root.style.setProperty('--sa-maroon-deep', adjustLightness(primaryColor, -15));

      // Derive semi-transparent variants
      const { h, s } = hexToHSL(primaryColor);
      root.style.setProperty('--sa-primary-light', `hsla(${h}, ${s}%, 35%, 0.08)`);
      root.style.setProperty('--sa-primary-glow', `hsla(${h}, ${s}%, 35%, 0.35)`);

      // Derive text colors
      root.style.setProperty('--sa-text-dark', adjustLightness(primaryColor, -12));
      root.style.setProperty('--sa-text', adjustLightness(primaryColor, 5));
      root.style.setProperty('--sa-text-light', adjustLightness(primaryColor, 20));
      root.style.setProperty('--sa-text-muted', adjustLightness(primaryColor, 35));

      // Derive border colors
      root.style.setProperty('--sa-border', adjustLightness(primaryColor, 55));
      root.style.setProperty('--sa-border-hover', adjustLightness(primaryColor, 45));

      // Chart accents
      root.style.setProperty('--sa-accent-light', adjustLightness(primaryColor, 40));
      root.style.setProperty('--sa-accent-muted', adjustLightness(primaryColor, 30));

      // Derive full dark theme palette from primary color (backgrounds + accents share same hue)
      const dark = deriveDarkPalette(primaryColor);
      root.style.setProperty('--sa-dark-bg', dark.bg);
      root.style.setProperty('--sa-dark-bg-end', dark.bgEnd);
      root.style.setProperty('--sa-dark-surface', dark.surface);
      root.style.setProperty('--sa-dark-surface-light', dark.surfaceLight);
      root.style.setProperty('--sa-input-bg', dark.surfaceLight);
      root.style.setProperty('--sa-dark-accent', dark.accent);
      root.style.setProperty('--sa-dark-accent-light', dark.accentLight);
      root.style.setProperty('--sa-dark-accent-muted', dark.accentMuted);
      root.style.setProperty('--sa-dark-text', dark.text);
      root.style.setProperty('--sa-dark-text-muted', dark.textMuted);
      root.style.setProperty('--sa-dark-border', dark.border);

      // RGB channels for branded translucent tints (rgba(var(--sa-dark-accent-rgb), alpha))
      root.style.setProperty('--sa-dark-accent-rgb', hexToRgbStr(dark.accent));
      root.style.setProperty('--sa-dark-accent-light-rgb', hexToRgbStr(dark.accentLight));
    }

    if (currentBranding['secondaryColor']) root.style.setProperty('--secondary-color', currentBranding['secondaryColor']);
    if (currentBranding['textColor']) root.style.setProperty('--text-color', currentBranding['textColor']);
    if (currentBranding['textwhite']) root.style.setProperty('--textwhite-color', currentBranding['textwhite']);
  }, [currentBranding?.['primaryColor'], currentBranding?.['secondaryColor'], isCustomBranding]);

  // Update favicon dynamically
  useEffect(() => {
    if (!currentBranding?.['favicon']) return;

    const updateFavicon = (faviconUrl) => {
      // Remove existing favicons
      const existingFavicons = document.querySelectorAll('link[rel="shortcut icon"], link[rel="icon"]');
      existingFavicons.forEach(favicon => favicon.remove());

      // Add new favicon
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'shortcut icon';
      newFavicon.type = 'image/x-icon';
      newFavicon.href = faviconUrl;
      document.head.appendChild(newFavicon);

      // Also add a regular icon link
      const iconLink = document.createElement('link');
      iconLink.rel = 'icon';
      iconLink.type = 'image/x-icon';
      iconLink.href = faviconUrl;
      document.head.appendChild(iconLink);

    };

    updateFavicon(currentBranding['favicon']);
  }, [currentBranding?.['favicon']]);

  // Update title for auth pages (where Sidebar is not present)
  useEffect(() => {
    if (!currentBranding?.['agencyName']) return;

    // Only update if we're on auth pages (title starts with specific auth page names or is 'Loading...')
    const currentTitle = document.title;
    if (currentTitle === 'Loading...' ||
        currentTitle.startsWith('Login') ||
        currentTitle.startsWith('Sign Up') ||
        currentTitle.startsWith('Forgot Password') ||
        currentTitle.startsWith('Reset Password')) {
      // Extract the page name (everything before the first '|' or use current title if no '|')
      const pageName = currentTitle.includes('|')
        ? currentTitle.split('|')[0].trim()
        : (currentTitle === 'Loading...' ? '' : currentTitle);

      if (pageName && pageName !== 'Loading...') {
        document.title = `${pageName} | ${currentBranding['agencyName']}`;
      } else {
        document.title = currentBranding['agencyName'];
      }
    }
  }, [currentBranding?.['agencyName']]);

  // Note: Document title is managed by Sidebar component for main app pages
  // BrandingProvider handles favicon, theme colors, and auth page titles

  return null; // Component only applies side effects, doesn't render anything
};

export default BrandingProvider;
